// src/pages/ContentSources.jsx
//
// Unified source approval queue — RSS feeds, Discord servers, and anything
// else Claire finds. Filter by kind and status. One place for everything.
import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient";

const FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL;
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const STATUS_STYLES = {
  pending_review: "bg-yellow-100 text-yellow-800",
  approved:       "bg-green-100 text-green-800",
  revoked:        "bg-gray-200 text-gray-600",
  rejected:       "bg-gray-200 text-gray-600",
};

const KIND_STYLES = {
  rss:     "bg-purple-50 text-purple-700",
  discord: "bg-indigo-50 text-indigo-700",
};

const KIND_LABELS = {
  rss:     "RSS",
  discord: "Discord",
};

// "Ask Claire" panel — only for RSS sources (has a converse function)
function DiscussPanel({ source }) {
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history]);

  async function send(e) {
    e.preventDefault();
    const text = message.trim();
    if (!text || busy) return;
    setBusy(true);
    setError("");
    const newHistory = [...history, { role: "user", text }];
    setHistory(newHistory);
    setMessage("");
    try {
      const res = await fetch(`${FUNCTIONS_URL}/converse-rss-source`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
        body: JSON.stringify({
          source: { url: source.url, theme: source.theme, reason: source.reason },
          history,
          message: text,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Request failed");
      setHistory([...newHistory, { role: "claire", text: json.reply }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded bg-gray-50 p-3 border border-gray-200">
      <div className="text-xs font-medium text-gray-500 mb-2">Ask Claire about this source</div>
      {history.length === 0 && (
        <p className="text-xs text-gray-400 mb-2">
          Ask why she suggested it, what you'd get from it, or whether your doubts make sense.
        </p>
      )}
      {history.length > 0 && (
        <div className="flex flex-col gap-2 mb-3 max-h-64 overflow-y-auto">
          {history.map((h, i) => (
            <div key={i} className={`rounded p-2 text-sm ${h.role === "user" ? "bg-white border ml-4" : "bg-blue-50 mr-4"}`}>
              <span className="text-xs font-medium text-gray-400 block mb-0.5">{h.role === "user" ? "You" : "Claire"}</span>
              {h.text}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}
      {error && <div className="mb-2 text-xs text-red-600">{error}</div>}
      <form onSubmit={send} className="flex gap-2">
        <input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Ask a question…"
          disabled={busy}
          className="flex-1 rounded border p-2 text-sm disabled:opacity-50"
        />
        <button type="submit" disabled={busy || !message.trim()}
          className="rounded bg-blue-500 px-3 py-2 text-sm text-white hover:bg-blue-600 disabled:opacity-50">
          {busy ? "…" : "Ask"}
        </button>
      </form>
    </div>
  );
}

function SourceCard({ s, onSetStatus, onDelete, busy }) {
  const [discussOpen, setDiscussOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const isDiscord = s.kind === "discord";
  const link = isDiscord ? s.invite_url : s.url;
  const approveLabel = isDiscord ? "Join this server" : "Approve";

  function submitRevoke() {
    onSetStatus(s.id, isDiscord ? "rejected" : "revoked", rejectNote.trim() || null);
    setRejectOpen(false);
    setRejectNote("");
  }

  const isRevoked = s.status === "revoked" || s.status === "rejected";

  return (
    <div className="rounded bg-white p-4 shadow">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded px-2 py-1 text-xs font-medium ${KIND_STYLES[s.kind] || "bg-gray-100 text-gray-600"}`}>
          {KIND_LABELS[s.kind] || s.kind}
        </span>
        <span className={`rounded px-2 py-1 text-xs font-medium ${STATUS_STYLES[s.status] || STATUS_STYLES.pending_review}`}>
          {s.status.replace("_", " ")}
        </span>
        {s.theme && <span className="rounded bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">#{s.theme}</span>}
        {s.is_speculative && (
          <span className="rounded bg-orange-50 px-2 py-1 text-xs font-medium text-orange-600">unverified link</span>
        )}
        <span className="ml-auto text-xs text-gray-500">{new Date(s.inserted_at).toLocaleDateString()}</span>
      </div>

      {s.name && <div className="mt-2 font-semibold text-gray-900">{s.name}</div>}

      {link && (
        <a href={link} target="_blank" rel="noreferrer"
          className="mt-1 block break-all text-sm font-medium text-blue-600 underline">
          {link}
        </a>
      )}
      {s.reason && <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{s.reason}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        {s.status !== "approved" && (
          <button type="button" disabled={busy} onClick={() => onSetStatus(s.id, "approved")}
            className="rounded bg-green-500 px-3 py-1.5 text-sm text-white hover:bg-green-600 disabled:opacity-50">
            {approveLabel}
          </button>
        )}
        {!isRevoked && (
          <button type="button" disabled={busy}
            onClick={() => { setRejectOpen((v) => !v); setDiscussOpen(false); }}
            className="rounded bg-red-100 px-3 py-1.5 text-sm text-red-700 hover:bg-red-200 disabled:opacity-50">
            No (+ explain why)
          </button>
        )}
        <button type="button" disabled={busy} onClick={() => onDelete(s.id)}
          className="rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-200 disabled:opacity-50">
          Delete
        </button>
        {!isDiscord && (
          <button type="button"
            onClick={() => { setDiscussOpen((v) => !v); setRejectOpen(false); }}
            className="rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200">
            {discussOpen ? "Close discussion" : "Ask Claire"}
          </button>
        )}
      </div>

      {rejectOpen && (
        <div className="mt-3 rounded border border-red-100 bg-red-50 p-3">
          <p className="mb-2 text-xs text-gray-500">Optional: tell Claire why, so she learns what to skip.</p>
          <textarea value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
            placeholder={isDiscord ? "e.g. too general, not serious collectors…" : "e.g. too crypto-focused, not enough visual art content…"}
            rows={2} className="w-full rounded border p-2 text-sm" />
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={submitRevoke}
              className="rounded bg-red-500 px-3 py-1.5 text-sm text-white hover:bg-red-600">
              Reject
            </button>
            <button type="button" onClick={() => { setRejectOpen(false); setRejectNote(""); }}
              className="rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200">
              Cancel
            </button>
          </div>
        </div>
      )}

      {discussOpen && <DiscussPanel source={s} />}
    </div>
  );
}

export default function ContentSources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState("");
  const [busyIds, setBusyIds] = useState(new Set());
  const [suggestingRss, setSuggestingRss] = useState(false);
  const [suggestingDiscord, setSuggestingDiscord] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [kindFilter, setKindFilter] = useState("all");

  async function fetchAll() {
    setLoading(true);
    const { data, error: fetchErr } = await supabase
      .from("discovery_sources")
      .select("*")
      .in("kind", ["rss", "discord"])
      .order("inserted_at", { ascending: false });
    if (fetchErr) setError(fetchErr.message);
    else setSources(data || []);
    setLoading(false);
  }

  useEffect(() => { fetchAll(); }, []);

  async function setStatus(id, status, reviewerNote = null) {
    setBusyIds((prev) => new Set(prev).add(id));
    const patch = reviewerNote ? { status, reviewer_notes: reviewerNote } : { status };
    const { error: updateErr } = await supabase.from("discovery_sources").update(patch).eq("id", id);
    if (updateErr) setError(updateErr.message);
    else setSources((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
    setBusyIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
  }

  async function deleteSource(id) {
    setBusyIds((prev) => new Set(prev).add(id));
    const { error: delErr } = await supabase.from("discovery_sources").delete().eq("id", id);
    if (delErr) setError(delErr.message);
    else setSources((prev) => prev.filter((s) => s.id !== id));
    setBusyIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
  }

  async function suggest(fn, setSuggesting) {
    setSuggesting(true);
    setError("");
    setBanner("");
    try {
      const res = await fetch(`${FUNCTIONS_URL}/${fn}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Suggestion failed");
      setBanner(`Proposed ${json.proposed} new suggestion(s)${json.skipped_duplicate ? `, ${json.skipped_duplicate} already known` : ""}.`);
      await fetchAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setSuggesting(false);
    }
  }

  const visible = sources.filter((s) => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (kindFilter !== "all" && s.kind !== kindFilter) return false;
    return true;
  });

  const pendingCount = sources.filter((s) => s.status === "pending_review").length;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Sources</h1>
            {pendingCount > 0 && (
              <p className="text-sm text-amber-600 font-medium">{pendingCount} pending review</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="rounded border p-2 text-sm">
              <option value="all">All types</option>
              <option value="rss">RSS feeds</option>
              <option value="discord">Discord servers</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded border p-2 text-sm">
              <option value="pending_review">Pending review</option>
              <option value="approved">Approved</option>
              <option value="revoked">Rejected</option>
              <option value="all">All statuses</option>
            </select>
            <button type="button" onClick={() => suggest("suggest-rss-sources", setSuggestingRss)}
              disabled={suggestingRss}
              className="rounded bg-purple-500 px-3 py-2 text-sm text-white hover:bg-purple-600 disabled:opacity-50">
              {suggestingRss ? "Searching…" : "Suggest RSS"}
            </button>
            <button type="button" onClick={() => suggest("suggest-discord-servers", setSuggestingDiscord)}
              disabled={suggestingDiscord}
              className="rounded bg-indigo-500 px-3 py-2 text-sm text-white hover:bg-indigo-600 disabled:opacity-50">
              {suggestingDiscord ? "Researching…" : "Suggest Discord"}
            </button>
          </div>
        </div>

        <p className="mb-4 text-sm text-gray-500">
          Everything Claire finds and wants to plug into — RSS feeds she reads, Discord servers she wants to join.
          Approve to activate. "Ask Claire" (RSS only) lets you question her reasoning before deciding.
        </p>

        {error && <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        {banner && <div className="mb-4 rounded bg-blue-50 p-3 text-sm text-blue-800">{banner}</div>}

        {loading ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="rounded bg-white p-6 text-center text-sm text-gray-500 shadow">
            Nothing here. Use the buttons above to have Claire suggest sources.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {visible.map((s) => (
              <SourceCard key={s.id} s={s} onSetStatus={setStatus} onDelete={deleteSource} busy={busyIds.has(s.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
